/**
 * Add-connection / share form (step 9a).
 *
 * One small form, reused in TWO modes per docs/plans/2026-05-15_SHARE_PRIMITIVE.md
 * § "Connections settings tab + add-connection form":
 *   - `connection` — bootstrap/manage a connection (Settings tab here)
 *   - `share`     — the future Share dialog (9d) — same fields, different hints
 *
 * The two modes differ ONLY in the field hints. Behaviour (validation,
 * submit, refresh) is identical.
 */
import { useState } from 'react'
import { Mail, Building2, User } from 'lucide-react'
import { errorText } from '@papan/api-client'

export type ConnectionFormMode = 'connection' | 'share'

export interface ConnectionFormValues {
  isOrganization: boolean
  organizationName: string
  email: string
  setAsDefaultContact: boolean
}

interface Props {
  mode: ConnectionFormMode
  /** Submit handler — owner reaches into orgConnectionsApi or wraps it. */
  onSubmit: (values: ConnectionFormValues) => Promise<void>
  onCancel: () => void
  /** Override the submit button label (e.g. "Confirm" when the form just
   *  stages a recipient instead of sending). */
  submitLabel?: string
  /** Connection-mode share-dialog hint: "already connected to <org>" */
  alreadyConnectedHint?: string | null
  /** Share-mode email hint: "known contact at this org" */
  knownContactHint?: string | null
  /** Existing connections to offer as one-click recipients (share mode).
   *  Picking one fills the form; typing a matching email shows an
   *  "already connected — delivered instantly" hint. */
  knownContacts?: Array<{ email: string; orgName: string; isOrganization: boolean }>
  /** Default-contact prefill in connection-mode for connected orgs */
  initialEmail?: string
  initialIsOrganization?: boolean
  /** Prefill the organization-name field (e.g. editing a staged recipient). */
  initialOrganizationName?: string
  /** Prefill the "set as default contact" toggle (e.g. re-editing a card). */
  initialSetAsDefaultContact?: boolean
  /** Email-only mode: the organization is already known/fixed (editing the
   *  contact address of a connected recipient), so hide the target-type
   *  segmented control and the org-name field and edit ONLY the email (the
   *  "set as default contact" toggle stays — that's how a correction gets
   *  persisted on the connection). */
  lockOrganization?: boolean
}

export function ConnectionForm({
  mode,
  onSubmit,
  onCancel,
  submitLabel,
  alreadyConnectedHint = null,
  knownContactHint = null,
  knownContacts,
  initialEmail = '',
  initialIsOrganization = true,
  initialOrganizationName = '',
  initialSetAsDefaultContact = false,
  lockOrganization = false,
}: Props) {
  const [isOrganization, setIsOrganization] = useState(initialIsOrganization)
  const [organizationName, setOrganizationName] = useState(initialOrganizationName)
  const [email, setEmail] = useState(initialEmail)
  const [setAsDefaultContact, setSetAsDefaultContact] = useState(initialSetAsDefaultContact)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit =
    email.includes('@') &&
    (lockOrganization || !isOrganization || organizationName.trim().length > 0)

  // The typed email matches an existing connection ⇒ this share lands
  // instantly (silent re-share, no handshake). Drives the green hint.
  const matchedContact = knownContacts?.find(
    c => c.email.trim().toLowerCase() === email.trim().toLowerCase(),
  )

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return
    setError(null)
    setSubmitting(true)
    try {
      await onSubmit({
        isOrganization,
        organizationName: organizationName.trim(),
        email: email.trim(),
        setAsDefaultContact,
      })
    } catch (err) {
      const text = errorText(err, 'Failed to submit')   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
    } finally {
      setSubmitting(false)
    }
  }

  const isShareMode = mode === 'share'

  return (
    <div className="space-y-3 p-3 rounded-md border themed-border">
      {/* Quick-pick — share with an org you're already connected to. One
          click fills the form; the share then lands instantly (no handshake). */}
      {!lockOrganization && knownContacts && knownContacts.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs themed-text-muted">Share with a connection:</p>
          <div className="flex flex-wrap gap-1.5">
            {knownContacts.map(c => {
              const active = email.trim().toLowerCase() === c.email.toLowerCase()
              return (
                <button
                  key={c.email}
                  type="button"
                  onClick={() => { setEmail(c.email); setOrganizationName(c.orgName); setIsOrganization(c.isOrganization) }}
                  title={c.email}
                  className={`px-2 py-1 text-xs rounded border transition-colors max-w-[12rem] truncate ${
                    active ? 'bg-blue-600 text-white border-blue-600' : 'themed-text-muted themed-border hover:themed-text'
                  }`}
                >
                  {c.orgName || c.email}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* is_organization — segmented control. Two full-width segments;
          the active one is highlighted blue. Label above explains the
          choice; segments are just "a Person" / "an Organization". Hidden
          in lock mode — the recipient org is already known/fixed. */}
      {!lockOrganization && (
      <div className="space-y-1.5">
        <p className="text-xs themed-text-muted">
          {isShareMode
            ? 'Please choose a share target:'
            : 'Please choose a connection target:'}
        </p>
        <div
          role="radiogroup"
          aria-label={isShareMode ? 'Share target type' : 'Connection target type'}
          className="flex w-full rounded-md border themed-border overflow-hidden"
        >
          <button
            type="button"
            role="radio"
            aria-checked={!isOrganization}
            onClick={() => setIsOrganization(false)}
            className={`flex-1 h-10 px-3 text-sm flex items-center justify-center gap-1.5 transition-colors ${
              !isOrganization
                ? 'bg-blue-600 text-white'
                : 'themed-text-muted hover:themed-text hover:themed-bg-secondary'
            }`}
          >
            <User size={14} />
            a Person
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={isOrganization}
            onClick={() => setIsOrganization(true)}
            className={`flex-1 h-10 px-3 text-sm flex items-center justify-center gap-1.5 transition-colors border-l themed-border ${
              isOrganization
                ? 'bg-blue-600 text-white'
                : 'themed-text-muted hover:themed-text hover:themed-bg-secondary'
            }`}
          >
            <Building2 size={14} />
            an Organization
          </button>
        </div>
      </div>
      )}

      {/* organization_input — org mode only. grid-rows trick: animate
          0fr ↔ 1fr so the modal expands/collapses smoothly instead of
          jumping when toggling Person ↔ Organization. Collapsed in lock
          mode (the org name is fixed). */}
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isOrganization && !lockOrganization ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
        aria-hidden={!isOrganization || lockOrganization}
      >
        <div className="overflow-hidden">
          <div className="space-y-1">
            <label className="text-xs themed-text-muted">Organization name</label>
            <input
              type="text"
              value={organizationName}
              onChange={e => setOrganizationName(e.target.value)}
              placeholder="Enter company name"
              tabIndex={isOrganization ? 0 : -1}
              className="w-full themed-input border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500"
            />
            {alreadyConnectedHint && (
              <p className="text-xs text-blue-400">{alreadyConnectedHint}</p>
            )}
          </div>
        </div>
      </div>

      {/* email */}
      <div className="space-y-1">
        <label className="text-xs themed-text-muted flex items-center gap-1">
          <Mail size={12} /> {isOrganization ? 'Contact email at this organization' : 'Email'}
        </label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Enter recipient email"
          onKeyDown={e => { if (e.key === 'Enter') void handleSubmit() }}
          className="w-full themed-input border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500"
        />
        {matchedContact ? (
          <p className="text-xs text-green-400">
            Already connected{matchedContact.orgName ? <> to <span className="font-medium">{matchedContact.orgName}</span></> : null} — this share is delivered instantly.
          </p>
        ) : knownContactHint ? (
          <p className="text-xs themed-text-muted">{knownContactHint}</p>
        ) : null}
      </div>

      {/* set_as_default_contact — org mode only. Same grid-rows
          animation as the org-name row above so toggling Person ↔
          Organization expands/collapses both sections together. */}
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isOrganization ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
        aria-hidden={!isOrganization}
      >
        <div className="overflow-hidden">
          <div className="flex items-start justify-between gap-3">
            <span className="text-xs themed-text-muted flex-1">
              Set as the default contact for this organization
              <span className="block themed-text-muted opacity-70">
                {isShareMode
                  ? "The Share dialog will pre-fill this address next time."
                  : "Future shares to this org will use this address by default."}
              </span>
            </span>
            <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={setAsDefaultContact}
                onChange={e => setSetAsDefaultContact(e.target.checked)}
                tabIndex={isOrganization ? 0 : -1}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-zinc-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => void handleSubmit()}
          disabled={!canSubmit || submitting}
          className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors"
        >
          {submitting ? '...' : (submitLabel ?? (isShareMode ? 'Send share' : 'Send connection request'))}
        </button>
        <button
          onClick={onCancel}
          className="text-xs themed-text-muted hover:themed-text transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
